import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, AlertTriangle, Sparkles, MapPin, Package, Heart, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";

type Msg = { role: "user" | "assistant"; content: string };

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/conflict-assistant`;

const quickActions = [
  { id: "explain", icon: Sparkles, label: "Explain Event", placeholder: "Describe the event you want to understand (e.g., 'missile strike near Kyiv today')..." },
  { id: "summarize", icon: MapPin, label: "Summarize Area", placeholder: "Enter a location (e.g., 'Tel Aviv', 'Eastern Ukraine', 'Gaza City')..." },
  { id: "kit", icon: Package, label: "Plan 72h Kit", placeholder: "Describe your situation (e.g., 'family of 4, apartment in Dubai, near military base')..." },
  { id: "children", icon: Heart, label: "Talk to Children", placeholder: "Describe your children's ages and situation (e.g., '2 kids, ages 5 and 9, hearing sirens at night')..." },
] as const;

const ConflictAssistant = () => {
  const { isPremium } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50);
  }, [messages]);

  const streamChat = useCallback(async (allMessages: Msg[], action?: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) throw new Error("Please sign in to use the AI Assistant.");

    const resp = await fetch(CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({ messages: allMessages, action }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: "Connection failed" }));
      throw new Error(err.error || `Error ${resp.status}`);
    }
    if (!resp.body) throw new Error("No response stream");

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let textBuffer = "";
    let assistantSoFar = "";

    const upsert = (chunk: string) => {
      assistantSoFar += chunk;
      const content = assistantSoFar;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          return prev.map((m, i) => i === prev.length - 1 ? { ...m, content } : m);
        }
        return [...prev, { role: "assistant", content }];
      });
    };

    let streamDone = false;
    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) break;
      textBuffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);
        if (line.endsWith("\r")) line = line.slice(0, -1);
        if (line.startsWith(":") || line.trim() === "") continue;
        if (!line.startsWith("data: ")) continue;
        const jsonStr = line.slice(6).trim();
        if (jsonStr === "[DONE]") { streamDone = true; break; }
        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content;
          if (content) upsert(content);
        } catch {
          textBuffer = line + "\n" + textBuffer;
          break;
        }
      }
    }

    // Flush remaining
    if (textBuffer.trim()) {
      for (let raw of textBuffer.split("\n")) {
        if (!raw) continue;
        if (raw.endsWith("\r")) raw = raw.slice(0, -1);
        if (raw.startsWith(":") || raw.trim() === "") continue;
        if (!raw.startsWith("data: ")) continue;
        const jsonStr = raw.slice(6).trim();
        if (jsonStr === "[DONE]") continue;
        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content;
          if (content) upsert(content);
        } catch { /* ignore */ }
      }
    }
  }, []);

  const send = async (text?: string) => {
    const content = text || input.trim();
    if (!content || isLoading) return;

    const userMsg: Msg = { role: "user", content };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    try {
      await streamChat(newMessages, activeAction || undefined);
    } catch (e: any) {
      setMessages(prev => [...prev, { role: "assistant", content: `⚠️ ${e.message}` }]);
    } finally {
      setIsLoading(false);
      setActiveAction(null);
    }
  };

  const handleQuickAction = (actionId: string) => {
    setActiveAction(actionId);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const currentAction = quickActions.find(a => a.id === activeAction);

  if (!isPremium) return null;

  return (
    <>
      {/* Floating button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-20 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/25 transition-transform hover:scale-110 md:bottom-6 md:right-6"
          >
            <MessageCircle className="h-6 w-6" />
            <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-warning text-[10px] font-bold text-warning-foreground">
              AI
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 z-50 flex h-[85vh] sm:h-[520px] w-full sm:w-[360px] flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl border border-border bg-card shadow-2xl sm:inset-x-auto sm:bottom-20 sm:right-2 md:bottom-6 md:right-6 md:h-[600px] md:w-[400px]"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border bg-secondary/50 px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Conflict Assistant</h3>
                  <p className="text-[10px] text-muted-foreground">AI-powered • Premium</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Disclaimer */}
            <div className="flex items-start gap-2 border-b border-border bg-warning/5 px-3 py-2">
              <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0 text-warning" />
              <p className="text-[10px] leading-tight text-muted-foreground">
                AI assistant for educational & planning support only. Does not replace official advisories from authorities.
              </p>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
              {messages.length === 0 && (
                <div className="space-y-3 pt-2">
                  <p className="text-center text-xs text-muted-foreground">
                    Choose a quick action or type your question below.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {quickActions.map(({ id, icon: Icon, label }) => (
                      <button
                        key={id}
                        onClick={() => handleQuickAction(id)}
                        className={`flex items-center gap-2 rounded-lg border p-3 text-left text-xs font-medium transition-all ${
                          activeAction === id
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border bg-secondary/50 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed ${
                      msg.role === "user"
                        ? "rounded-br-md bg-primary text-primary-foreground"
                        : "rounded-bl-md border border-border bg-secondary/50 text-foreground"
                    }`}
                  >
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  </div>
                </div>
              ))}

              {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2 rounded-2xl rounded-bl-md border border-border bg-secondary/50 px-3.5 py-2.5 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Analyzing...
                  </div>
                </div>
              )}
            </div>

            {/* Quick actions (shown when messages exist) */}
            {messages.length > 0 && !isLoading && (
              <div className="flex gap-1.5 overflow-x-auto border-t border-border bg-secondary/30 px-3 py-2">
                {quickActions.map(({ id, label }) => (
                  <Badge
                    key={id}
                    variant="outline"
                    className={`cursor-pointer whitespace-nowrap text-[10px] transition-colors hover:border-primary/40 ${
                      activeAction === id ? "border-primary bg-primary/10 text-primary" : ""
                    }`}
                    onClick={() => handleQuickAction(id)}
                  >
                    {label}
                  </Badge>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="border-t border-border p-3">
              {currentAction && (
                <div className="mb-2 flex items-center gap-1.5">
                  <Badge variant="secondary" className="text-[10px]">
                    {currentAction.label}
                  </Badge>
                  <button onClick={() => setActiveAction(null)} className="text-[10px] text-muted-foreground hover:text-foreground">✕</button>
                </div>
              )}
              <div className="flex items-end gap-2">
                <Textarea
                  ref={textareaRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={currentAction?.placeholder || "Ask about a conflict event, area, or emergency planning..."}
                  className="min-h-[40px] max-h-[80px] resize-none border-border bg-secondary/50 text-xs"
                  rows={1}
                  disabled={isLoading}
                />
                <Button
                  size="icon"
                  className="h-[40px] w-[40px] flex-shrink-0"
                  onClick={() => send()}
                  disabled={!input.trim() || isLoading}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ConflictAssistant;
